import type { IconName } from "@@/shared/icon-name";
import {
    translation,
    type TranslatableValue,
} from "@@/shared/translations/translation";
import { toObjectMap } from "@@/utilities/dictionary";
import { z } from "zod";

const accommodationQuantityTypes = [
    "ADULT",
    "CHILD",
    "INFANT",
    "GUEST",
    "ANIMAL",
] as const;

type AccommodationQuantityType = (typeof accommodationQuantityTypes)[number];
const accommodationQuantityTypeMap = toObjectMap<AccommodationQuantityType>([
    ...accommodationQuantityTypes,
]);
const accommodationQuantityTypeZodEnum = z.enum(accommodationQuantityTypes);

const accommodationQuantityTypesCountedAsGuest = ["ADULT", "CHILD"] as const;
type AccommodationQuantityTypeCountedAsGuest =
    (typeof accommodationQuantityTypesCountedAsGuest)[number];
// const otherAccommodationQuantityTypesCountedAsGuestMap =
//     toObjectMap<OtherAccommodationQuantityTypeCountedAsGuest>([
//         ...otherAccommodationQuantityTypesCountedAsGuest,
//     ]);
// const otherAccommodationQuantityTypesCountedAsGuestZodEnum = z.enum(
//     otherAccommodationQuantityTypesCountedAsGuest,
// );

type AccommodationQuantityTypeMeta = {
    [key in AccommodationQuantityType]: {
        iconName: IconName;
        name: {
            /** Example output: `Adult`
             * @returns {TranslatableValue} */
            singular: TranslatableValue;
            /** Example output: `Adults`
             * @returns {TranslatableValue} */
            plural: TranslatableValue;
            /** Example output: `3 adults`
             * @returns {(value: number ) => TranslatableValue} */
            withQuantity: (value: number) => TranslatableValue;
        };
        subTitle: TranslatableValue;
    };
};

const accommodationQuantityTypeMeta: AccommodationQuantityTypeMeta = {
    ADULT: {
        iconName: "fa-person",
        name: {
            plural: translation({
                sv: "Vuxna",
                en: "Adults",
            }),
            withQuantity: (value: number) =>
                translation({
                    sv: `${value} vuxna`,
                    en: `${value} adults`,
                }),
            singular: translation({
                sv: "Vuxen",
                en: "Adult",
            }),
        },
        subTitle: translation({
            sv: "18 år och äldre",
            en: "18 years and older",
        }),
    },
    CHILD: {
        iconName: "fa-child",
        name: {
            plural: translation({
                sv: "Barn",
                en: "Children",
            }),
            withQuantity: (value: number) =>
                translation({
                    sv: `${value} Barn`,
                    en: `${value} Children`,
                }),
            singular: translation({
                sv: "Barn",
                en: "Child",
            }),
        },
        subTitle: translation({
            sv: "2-17 år",
            en: "2-17 years",
        }),
    },
    INFANT: {
        iconName: "fa-baby-carriage",
        name: {
            plural: translation({
                sv: "Spädbarn",
                en: "Infants",
            }),
            withQuantity: (value: number) =>
                translation({
                    sv: `${value} Spädbarn`,
                    en: `${value} Infants`,
                }),
            singular: translation({
                sv: "Spädbarn",
                en: "Infant",
            }),
        },
        subTitle: translation({
            sv: "Under 2 år",
            en: "Under 2 years",
        }),
    },
    GUEST: {
        iconName: "fa-family",
        name: {
            plural: translation({
                sv: "Gäster",
                en: "Guests",
            }),
            withQuantity: (value: number) =>
                translation({
                    sv: `${value} Gäster`,
                    en: `${value} Guests`,
                }),
            singular: translation({
                sv: "Gäst",
                en: "Guest",
            }),
        },
        subTitle: translation({
            sv: "Vuxna, barn och spädbarn",
            en: "Adults, children and infants",
        }),
    },
    ANIMAL: {
        iconName: "fa-dog",
        name: {
            plural: translation({
                sv: "Djur",
                en: "Animals",
            }),
            withQuantity: (value: number) =>
                translation({
                    sv: `${value} Djur`,
                    en: `${value} Animals`,
                }),
            singular: translation({
                sv: "Djur",
                en: "Animal",
            }),
        },
        subTitle: translation({
            sv: "Hundar, katter, etc.",
            en: "Dogs, cats, etc.",
        }),
    },
};

const isAccommodationQuantityType = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is AccommodationQuantityType =>
    accommodationQuantityTypes.includes(item);

const isAccommodationQuantityTypeCountedAsGuest = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    item: any,
): item is AccommodationQuantityType =>
    item === "GUEST" || accommodationQuantityTypesCountedAsGuest.includes(item);

export {
    accommodationQuantityTypeMap,
    accommodationQuantityTypeMeta,
    accommodationQuantityTypes,
    accommodationQuantityTypesCountedAsGuest,
    accommodationQuantityTypeZodEnum,
    isAccommodationQuantityType,
    isAccommodationQuantityTypeCountedAsGuest,
};
export type {
    AccommodationQuantityType,
    AccommodationQuantityTypeCountedAsGuest,
};
